import { storeToRefs } from 'pinia';
import { useHead, useSeoMeta } from 'unhead';

export default defineNuxtPlugin(async (nuxtApp: NuxtApp) => {
    console.log('[Route]: Init.');
    const router = useRouter();
    const img = useImage();
    const url = useRequestURL();
    const localePath = useLocalePath();

    const {
        $client,
        $i18n,
    } = nuxtApp;

    const {
        page,
        nodes,
        settings,
        commonCode,
        availability
    } = storeToRefs($client);

    let numFormat = {
        style: 'decimal',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
        useGrouping: false
    };

    const jsonLdBreadcrumb = computed(() => ({
        key: 'breadcrumbs-json-ld',
        type: 'application/ld+json',
        textContent: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: nodes.value.breadcrumbs.map((node: any, idx: number) => ({
                '@type': 'ListItem',
                position: idx + 1,
                item: {
                    '@id': new URL(localePath({ name: 'store-slug', params: { slug: node.path.split('/') } }), url.protocol + '//' + url.host).href,
                    name: node.name
                }
            }))
        })
    }));
    const jsonLdProductGroup = computed(() => {
        if (page.value.article) {
            var country = settings.value.countries.find((country: any) => country.id === settings.value.country);

            // https://developers.google.com/search/docs/appearance/structured-data/product-variants
            return {
                key: 'product-group-json-ld',
                type: 'application/ld+json',
                textContent: JSON.stringify([{
                    '@context': 'https://schema.org',
                    '@type': 'ProductGroup',
                    name: page.value.name,
                    description: page.value.description,
                    brand: {
                        '@type': 'Brand',
                        name: page.value.article.manufacturers
                    },
                    hasVariant: page.value.article.products.map((product: any, idx: number, products: Array<any>) => ({
                        '@type': 'Product',
                        '@id': `#${product.productId}-${product.discriminator}`,
                        sku: product.quantity === 1 ? product.productCode : `${product.productCode}-MPX${product.quantity}`,
                        [`gtin${product.productBarcode?.length}`]: product.productBarcode ?? undefined,
                        image: img(product.image?.name, undefined, { provider: 'twicpics' }),
                        name: `${product.productName} (${product.subtitle})`, // TODO: Products with no subtitle?
                        description: product.productTextShort || undefined,
                        // TODO: Översätt värderna. 
                        color: product.attributes.data.filter((kvp: any) => kvp.key === 'color').map((kvp: any) => kvp.value).join(', ') || undefined,
                        size: product.attributes.data.filter((kvp: any) => kvp.key === 'size').map((kvp: any) => kvp.value).join(', ') || undefined,
                        offers: {
                            '@type': "Offer",
                            priceSpecification: {
                                '@type': 'UnitPriceSpecification',
                                price: $i18n.n(product.prices[country.id]
                                    .filter(price => price.currency === country.currency)
                                    .map(price => (price.campaignPrice
                                        ? Math.round((price.campaignPrice + (settings.value.vat
                                            ? price.campaignTaxes.reduce((sum: number, a) => sum + a.amount, 0)
                                            : 0) + Number.EPSILON) * 100) / 100
                                        : Math.round((price.salesPrice + (settings.value.vat
                                            ? price.salesTaxes.reduce((sum: number, a) => sum + a.amount, 0)
                                            : 0) + Number.EPSILON) * 100) / 100
                                    ) * product.quantity)
                                    .find(() => true), numFormat, 'en'),
                                priceCurrency: country.currency,
                                referenceQuantity: {
                                    '@type': 'QuantitativeValue',
                                    value: Math.round(((product.weight * product.quantity) + Number.EPSILON) * 100) / 100,
                                    unitCode: commonCode.value[product.weightUnit],
                                },
                                includesObject: product.quantity > 1 ?
                                    (() => {
                                        const other = products.find((p) => p.productId === product.productId && p.quantity === 1);

                                        if (other) {
                                            return {
                                                amountOfThisGood: product.quantity,
                                                typeOfGood: {
                                                    '@id': `#${other.productId}-${other.discriminator}`
                                                }
                                            }
                                        }

                                        return undefined;
                                    })()
                                    : undefined
                            },
                            "itemCondition": "https://schema.org/NewCondition",
                            availability: availability.value[product.stockStatus],
                            "shippingDetails": { "@id": "#shipping_policy" },
                            //"hasMerchantReturnPolicy": { "@id": "#return_policy" }
                        }
                    }))
                }, {
                    "@context": "https://schema.org/",
                    "@type": "OfferShippingDetails",
                    "@id": "#shipping_policy",
                    "shippingDestination": {
                        "@type": "DefinedRegion",
                        "addressCountry": country.id
                    },
                    // 79 kr gäller bara för sverige men är med även för Danmark och Finland!!!
                    "shippingRate": {
                        "@type": "ShippingRateSettings",
                        "shippingRate": {
                            "@type": "MonetaryAmount",
                            "value": 79,
                            "currency": country.currency
                        },
                        "freeShippingThreshold": {
                            "@type": "MonetaryAmount",
                            "value": 900,
                            "currency": country.currency
                        }
                    },
                    "deliveryTime": {
                        "@type": "ShippingDeliveryTime",
                        "handlingTime": {
                            "@type": "QuantitativeValue",
                            "minValue": 0,
                            "maxValue": 1,
                            "unitCode": "DAY"
                        },
                        "transitTime": {
                            "@type": "QuantitativeValue",
                            "minValue": 1,
                            "maxValue": 5,
                            "unitCode": "DAY"
                        }
                    }
                }])
            }
        }

        return null;
    });

    router.afterEach((to: any) => {
        if (page.value) {
            let head = injectHead();
            const rawJson = page.value.properties?.['product-group-json-ld'];
            
            let jsonLd = {
                key: 'product-group-json-ld',
                type: 'application/ld+json',
                textContent: rawJson ? JSON.parse(rawJson) : ''
            };
            
            useHead(head, {
                title: page.value.title,
                script: () => [
                    jsonLdBreadcrumb.value,
                    jsonLd
                ],
                htmlAttrs: {
                    lang: page.value?.culture || $i18n.locale.value
                },
                link: [{
                    rel: 'canonical',
                    href: () => page.value?.canonical,
                }, ...Object.entries(page.value?.alternates)
                    .map(([k, v], i) => ({
                        rel: 'alternate',
                        hreflang: k,
                        href: v
                    }))],
            });

            useSeoMeta(head, {
                ogTitle: page.value.title,
                description: page.value.description,
                ogDescription: page.value.description,
                ogType: 'website',
                ogUrl: page.value.canonical,
                ogImage: page.value.image
                    ? img(page.value.image.name, { inside: '1200x630', quality: 70, fit: 'clamp', 'background': 'fff' }, { provider: 'twicpics' })
                    : null,
                ogImageWidth: page.value.image ? 1200 : null,
                ogImageHeight: page.value.image ? 630 : null
            });
        }
    });

    addRouteMiddleware('global-route-middleware', async (to, from) => {
        const url: string = to.params.slug
            ? `${$i18n.locale.value}/${to.params.slug.join('/')}`
            : to.path;

        await $client.route.execute(to, from, url);
    }, { global: true });
})